import { useState } from "react";
import {
  BadgeCheck,
  ChevronsUpDown,
  CreditCard,
  Keyboard,
  LogOut,
  Megaphone,
  Sparkles,
  Trash,
  Workflow,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";
import { useAuthChecker } from "../../Context";
import { ROUTE, UserType } from "../../Constant";
import { useNavigate } from "react-router-dom";
import { MoonOutlined, SunOutlined } from "@ant-design/icons";
import { useTheme } from "../../Hook";
import { ShortKeyPanel } from "../ShortKeyPanel/ShortKeyPanel";
import { UpgradePlanDrawer } from "./UpgradePlanDrawer";

export function NavUser({
  user,
  handleClearCache,
}: {
  user: UserType;
  handleClearCache: () => void;
}) {
  const { isMobile } = useSidebar();
  const { signOut } = useAuthChecker();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [shortkeysOpen, setShortkeysOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={""} alt={user?.username} />
                <AvatarFallback className="rounded-lg">CN</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user?.username}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={""} alt={user?.username} />
                  <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user?.username}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setUpgradeModalOpen(true)}
              >
                <Sparkles />
                Upgrade to Plan
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={handleClearCache}
              >
                <Trash />
                Clear Cache
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setShortkeysOpen(true)}
              >
                <Keyboard />
                Shortcut Keys
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => navigate(ROUTE.ad_list)}
              >
                <Megaphone />
                Advertisements
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => navigate(`/system/${ROUTE.billing}`)}
              >
                <CreditCard />
                Package & Billing
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  toggleTheme();
                }}
              >
                {isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                {isDarkMode ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => {
                signOut();
                navigate(ROUTE.login);
              }}
            >
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <ShortKeyPanel
        setShortkeysOpen={setShortkeysOpen}
        shortkeysOpen={shortkeysOpen}
      />

      <UpgradePlanDrawer
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
      />
    </SidebarMenu>
  );
}
